import Echo from "laravel-echo";
import Pusher from "pusher-js";
import API_ORIGIN from "../config";
import http from "./http";

type EchoChannel = {
  listen: <TPayload = unknown>(
    event: string,
    callback: (payload: TPayload) => void
  ) => EchoChannel;
  error: (callback: (error: unknown) => void) => void;
};

export type EchoPresenceChannel<
  THere = unknown[],
  TJoining = unknown,
  TLeaving = unknown
> = {
  here: (
    callback: (participants: THere) => void
  ) => EchoPresenceChannel<THere, TJoining, TLeaving>;
  joining: (
    callback: (participant: TJoining) => void
  ) => EchoPresenceChannel<THere, TJoining, TLeaving>;
  leaving: (
    callback: (participant: TLeaving) => void
  ) => EchoPresenceChannel<THere, TJoining, TLeaving>;
  error: (
    callback: (error: unknown) => void
  ) => EchoPresenceChannel<THere, TJoining, TLeaving>;
};

type EchoInstance = {
  private: (channelName: string) => EchoChannel;
  join: (channelName: string) => EchoPresenceChannel;
  leave: (channelName: string) => void;
};

declare global {
  interface Window {
    Echo?: EchoInstance;
    Pusher?: typeof Pusher;
  }
}

function getEnv(key: string, fallback = ""): string {
  return String(process.env[key] || fallback);
}

function getAuthEndpoint(): string {
  const routePrefix = process.env.VUE_APP_APP_ROUTE_PREFIX || "";
  const normalizedPrefix = routePrefix
    ? `/${routePrefix.replace(/^\/+|\/+$/g, "")}`
    : "";

  return API_ORIGIN
    ? `${API_ORIGIN}${normalizedPrefix}/api/broadcasting/auth`
    : `${normalizedPrefix}/api/broadcasting/auth`;
}

export function initializeEcho(): EchoInstance | null {
  if (typeof window === "undefined") {
    return null;
  }

  if (window.Echo) {
    return window.Echo;
  }

  if (getEnv("VUE_APP_REALTIME_ENABLED", "true") === "false") {
    return null;
  }

  const appKey =
    getEnv("VUE_APP_REVERB_APP_KEY") || getEnv("VUE_APP_REVERB_KEY");

  if (!appKey) {
    return null;
  }

  const host =
    getEnv("VUE_APP_REVERB_HOST") ||
    getEnv("VUE_APP_REVERB_SERVER_HOST") ||
    window.location.hostname;
  const port = Number(
    getEnv("VUE_APP_REVERB_PORT") ||
      getEnv("VUE_APP_REVERB_SERVER_PORT") ||
      8080
  );
  const scheme =
    getEnv("VUE_APP_REVERB_SCHEME") ||
    getEnv("VUE_APP_REVERB_SERVER_SCHEME") ||
    "http";

  window.Pusher = Pusher;
  const EchoCtor = Echo as unknown as new (
    options: Record<string, unknown>
  ) => EchoInstance;
  window.Echo = new EchoCtor({
    broadcaster: "reverb",
    key: appKey,
    wsHost: host,
    wsPort: port,
    wssPort: port,
    forceTLS: scheme === "https",
    enabledTransports: scheme === "https" ? ["wss"] : ["ws"],
    authorizer: (channel: { name: string }) => ({
      authorize: (
        socketId: string,
        callback: (error: Error | null, data?: unknown) => void
      ) => {
        http
          .post(getAuthEndpoint(), {
            socket_id: socketId,
            channel_name: channel.name,
          })
          .then((response) => callback(null, response.data))
          .catch((error) => callback(error));
      },
    }),
  });

  return window.Echo;
}
